import { createFileRoute } from '@tanstack/react-router';
import { dateKeyInTimeZone } from '@/lib/date-values';
import { Badge } from '@/components/atoms/badge';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { customerReportEndpoints } from '@/lib/api/endpoints';
import { customerReportKeys } from '@/lib/queryKeys';
import { channelColumns, type CustomerChannelRow } from './-channelColumns';
import { exportRowsAsXLSX } from '@/lib/export';
import {
  AnalyticsReportShell,
  MetricCard,
  ReportDateRangeControl,
  ReportLineChart,
  ReportSection,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  formatPercent,
  useDrillToList,
  useReportPeriod,
  type ScorecardColumn,
} from '@/components/organisms/analytics-reports';

interface CustomerOverview {
  totalCustomers: number;
  newCustomers: number;
  returningCustomers: number;
  repeatRate: number;
  avgRevenuePerCustomer: number;
  pctMembers: number;
}
interface CustomerRow {
  contactId: string;
  name: string;
  invoices: number;
  revenue: number;
  isMember: boolean;
  isNew: boolean;
}
interface CustomerReport {
  period: { from: string; to: string };
  overview: CustomerOverview;
  overviewPrevious: CustomerOverview;
  topCustomers: CustomerRow[];
  byChannel: CustomerChannelRow[];
  trend: { period: string; value: number }[];
}

export const Route = createFileRoute('/_dashboard/dashboard/contacts/reports')({
  component: CustomerReportsPage,
});

const columns: ScorecardColumn<CustomerRow>[] = [
  { key: 'name', header: 'Customer', type: 'text', value: (r) => r.name },
  {
    key: 'invoices',
    header: 'Invoices',
    type: 'number',
    value: (r) => r.invoices,
    total: (rows) => rows.reduce((s, r) => s + r.invoices, 0),
  },
  {
    key: 'revenue',
    header: 'Revenue',
    type: 'currency',
    value: (r) => r.revenue,
    total: (rows) => rows.reduce((s, r) => s + r.revenue, 0),
  },
  {
    key: 'type',
    header: 'Type',
    type: 'text',
    value: (r) => (r.isNew ? 'New' : 'Returning'),
    render: (r) => (
      <Badge variant={r.isNew ? 'default' : 'secondary'}>{r.isNew ? 'New' : 'Returning'}</Badge>
    ),
  },
  {
    key: 'member',
    header: 'Member',
    type: 'text',
    value: (r) => (r.isMember ? 'Yes' : 'No'),
    render: (r) => (r.isMember ? <Badge variant="outline">Member</Badge> : null),
  },
];

function CustomerReportsPage() {
  const period = useReportPeriod('analytics.customers.reportPeriod');
  const params = { from: period.from, to: period.to };
  const drillTo = useDrillToList();

  const q = useApiQuery<CustomerReport>({
    queryKey: customerReportKeys.report('summary', params),
    queryFn: () =>
      apiGet<CustomerReport>(operationsClient, customerReportEndpoints.summary(params)),
    showErrorToast: false,
  });
  const data = q.data;
  const o = data?.overview;
  const prev = data?.overviewPrevious;

  async function handleExport() {
    if (!data) return;
    const rows = data.topCustomers.map((r) => ({
      Customer: r.name,
      Invoices: r.invoices,
      Revenue: r.revenue,
      Type: r.isNew ? 'New' : 'Returning',
      Member: r.isMember ? 'Yes' : 'No',
    }));
    // Period bounds are UTC-snapped ISO instants; label the file with their UTC
    // calendar date (the same boundary the backend filters on).
    const fname = `customers-report-${dateKeyInTimeZone(period.from, 'UTC')}_${dateKeyInTimeZone(period.to, 'UTC')}.xlsx`;
    // The page shows two tables, so the workbook carries two sheets. Exporting
    // only the ranked customers silently dropped the lead-source table for
    // anyone who clicked Export instead of reading the screen.
    //
    // Money a user is not allowed to see is masked server-side and arrives
    // undefined; it is left out of the cell rather than written as 0, for the
    // same reason the table renders '--'.
    const channelRows = (data.byChannel ?? []).map((c) => ({
      'Lead source': c.channel,
      Customers: c.customers,
      Invoices: c.invoices,
      'Lifetime revenue': c.revenue ?? '',
      'First purchase': c.firstPurchaseRevenue ?? '',
      Repeat: c.repeatRevenue ?? '',
      'LTV / customer': c.avgRevenuePerCustomer ?? '',
    }));
    await exportRowsAsXLSX(rows, fname, {
      sheetName: 'Top customers',
      currencyColumns: ['Revenue'],
      extraSheets: [
        {
          name: 'LTV by lead source',
          rows: channelRows,
          currencyColumns: ['Lifetime revenue', 'First purchase', 'Repeat', 'LTV / customer'],
        },
      ],
    });
  }

  return (
    <AnalyticsReportShell
      title="Customer Reports"
      subtitle="Revenue, retention & lifetime value"
      backTo="/dashboard/contacts"
      onExport={handleExport}
      exportDisabled={!data}
      actions={<ReportDateRangeControl period={period} />}
    >
      <ReportSection
        title="Overview"
        description="Revenue is invoice-based (excludes drafts/void). A customer is 'returning' if they had activity before this range."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <MetricCard
            title="Customers"
            value={o ? formatInteger(o.totalCustomers) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.totalCustomers, prev?.totalCustomers)}
          />
          <MetricCard
            title="New"
            value={o ? formatInteger(o.newCustomers) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.newCustomers, prev?.newCustomers)}
          />
          <MetricCard
            title="Returning"
            value={o ? formatInteger(o.returningCustomers) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.returningCustomers, prev?.returningCustomers)}
          />
          <MetricCard
            title="Repeat Rate"
            value={o ? formatPercent(o.repeatRate) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.repeatRate, prev?.repeatRate)}
          />
          <MetricCard
            title="Avg Revenue / Customer"
            value={o ? formatCurrency(o.avgRevenuePerCustomer) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.avgRevenuePerCustomer, prev?.avgRevenuePerCustomer)}
          />
          <MetricCard
            title="% Members"
            value={o ? formatPercent(o.pctMembers) : '--'}
            isLoading={q.isLoading}
          />
        </div>
      </ReportSection>

      <ReportSection title="LTV by lead source">
        <ScorecardTable<CustomerChannelRow>
          rows={data?.byChannel ?? []}
          isLoading={q.isLoading}
          emptyMessage="No attributed customer revenue yet"
          initialSortKey="revenue"
          columns={channelColumns}
        />
      </ReportSection>

      <ReportSection title="Top customers">
        <ScorecardTable<CustomerRow>
          rows={data?.topCustomers ?? []}
          isLoading={q.isLoading}
          emptyMessage="No customer revenue in this range"
          initialSortKey="revenue"
          columns={columns}
          onRowClick={(r) => drillTo('contacts', { q: r.name })}
        />
      </ReportSection>

      <ReportSection title="New customers trend">
        <ReportLineChart
          title="New customers by month"
          isLoading={q.isLoading}
          points={(data?.trend ?? []).map((p) => ({ period: p.period, value: p.value }))}
        />
      </ReportSection>
    </AnalyticsReportShell>
  );
}
